/**
 * Custom Event Text — Domain API Module
 *
 * Wraps the shared V2 API client for all data operations on
 * alert notification format templates (core_event_text_template).
 *
 * Stored procedures:
 *   getalerttemplates — read all templates for a campus
 *
 * Table:
 *   core_event_text_template
 *
 * CRUD via apiSave (mysave endpoint):
 *   PUT    — add template
 *   POST   — edit template
 *   DELETE  — delete template(s)
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ
// ---------------------------------------------------------------------------

/** Fetch all alert text templates for a campus. */
export function fetchTemplates(campusId) {
  return apiFetch({
    procedure: 'getalerttemplates',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
  });
}

// ---------------------------------------------------------------------------
// WRITE
// ---------------------------------------------------------------------------

/**
 * Add a new alert text template.
 * @param {{ output_type_id: number, campus_id: number, text_format: string, modified_by: number }} payload
 * @param {object} session — { campusId, userId, clientId }
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section B): PalCare-only; only these columns are written, for the row's own campus. */
export function addTemplate(payload, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'event_text_template_add',
    payload: [payload],
    campusId,
    userId,
    clientId,
  });
}

/**
 * Edit an existing alert text template.
 * @param {object} payload — new values { template_id, campus_id, text_format }
 * @param {object} oldPayload — previous values for audit { template_id, campus_id, text_format }
 * @param {object} session — { campusId, userId, clientId }
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section B): PalCare-only; only these columns are written, for the row's own campus. */
export function editTemplate(payload, oldPayload, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'event_text_template_edit',
    payload: [payload],
    oldPayload: [oldPayload],
    campusId,
    userId,
    clientId,
  });
}

/**
 * Delete one or more alert text templates.
 * @param {Array<{ template_id: number }>} templateIds
 * @param {object} session — { campusId, userId, clientId }
 */
/* /myrest -> mysql_transaction (docs/MYREST_MIGRATION_AUDIT.md section B): PalCare-only; only these columns are written, for the row's own campus. */
export function deleteTemplates(templateIds, { campusId, userId, clientId }) {
  return apiTransaction({
    transType: 'event_text_template_delete',
    payload: templateIds,
    campusId,
    userId,
    clientId,
  });
}
